import { AlertDialog, Button } from '@buffer-mono/popcorn'

import { graphql, readFragment } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import type { JSX } from 'react'

/**
 * Colocated data contract for the delete-confirmation dialog. This leaf only
 * renders the reply's name/preview and never forwards the value to a
 * broad-`SavedReply`-typed consumer, so it selects only the fields it reads.
 */
export const DiscardDeleteSavedReplyDialog_SavedReply = graphql(/* GraphQL */ `
  fragment DiscardDeleteSavedReplyDialog_SavedReply on SavedReply @_unmask {
    title
    content {
      text
    }
  }
`)

type DiscardReplyDialogProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  savedReply: FragmentOf<typeof DiscardDeleteSavedReplyDialog_SavedReply>
  onCancelDeleting: () => void
  onKeepDeleting: () => void
}

export const DiscardDeleteSavedReplyDialog = ({
  open,
  onOpenChange,
  savedReply,
  onCancelDeleting,
  onKeepDeleting,
}: DiscardReplyDialogProps): JSX.Element => {
  const savedReplyData = readFragment(
    DiscardDeleteSavedReplyDialog_SavedReply,
    savedReply,
  )
  const savedReplyName = savedReplyData?.title
    ? savedReplyData.title.slice(0, 30)
    : savedReplyData.content.text.slice(0, 30)

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialog.Content>
        <AlertDialog.Title>Delete Saved Reply</AlertDialog.Title>
        <AlertDialog.Description>
          Your Saved Reply{' '}
          {savedReplyName ? <strong>&apos;{savedReplyName}&apos;</strong> : ''}{' '}
          will be deleted permanently. This cannot be undone.
        </AlertDialog.Description>
        <AlertDialog.Actions>
          <AlertDialog.Cancel onClick={onCancelDeleting}>
            <Button variant="tertiary">Cancel</Button>
          </AlertDialog.Cancel>
          <AlertDialog.Action onClick={onKeepDeleting}>
            <Button variant="critical">Delete Saved Reply</Button>
          </AlertDialog.Action>
        </AlertDialog.Actions>
      </AlertDialog.Content>
    </AlertDialog>
  )
}
